import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Observable, of } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'
import { DashboardJobLookupDto } from 'src/app/types/dto/dashboard-job-lookup.dto'
import {
  applyJobLookup,
  buildJobLookup,
  getMissingJobNumbers,
  JobLookup,
} from 'src/app/types/datasources/job-lookup.model'
import { environment } from 'src/environments/environment'
import { AuthService } from '../auth.service'
import { DatasourceConfigService } from '../chart-datasource/datasource-config.service'
import { HttpHelperService } from '../http.service'
import { LogService } from '../log.service'
import { TimedCache } from '../timed-cache'

@Injectable({
  providedIn: 'root',
})
export class JobLookupApiService {
  private http = inject(HttpClient)
  private httpHelper = inject(HttpHelperService)
  private auth = inject(AuthService)
  private logger = inject(LogService)
  private datasourceConfig = inject(DatasourceConfigService)

  private cachedLookup = new TimedCache<JobLookup>()
  private latestLookupByCompany = new Map<number, JobLookup>()

  constructor() {
    this.httpHelper.clearCaches.subscribe(() => {
      this.cachedLookup.clear()
      this.latestLookupByCompany.clear()
    })
  }

  getLookup(): Observable<JobLookup> {
    const comp = this.auth.getCurrentCompany()
    if (!comp) {
      this.logger.log('job-lookup.api', 'getLookup', null, 'no company')
      throw Error('no company set in getLookup')
    }

    return this.cachedLookup.get(comp.id, () =>
      this.http
        .get<DashboardJobLookupDto>(
          environment.apiUrl + 'jobs/dashboard-lookup',
          this.httpHelper.httpReadOnlyOptions
        )
        .pipe(
          map((dto) => buildJobLookup(dto)),
          tap((lookup) => {
            this.latestLookupByCompany.set(comp.id, lookup)
            this.datasourceConfig.setJobRolesForCompany(comp.id, lookup.roles)
          })
        )
    )
  }

  /** applies current job fields and roles to rows, refetching the lookup once if rows reference jobs it doesn't know (e.g. newly created jobs) */
  enrich<T extends object>(rows: T[]): Observable<T[]> {
    const companyId = this.auth.getCurrentCompany()?.id

    return this.getLookup().pipe(
      switchMap((lookup) => {
        if (companyId == null || !getMissingJobNumbers(rows, lookup).length) return of(lookup)

        // another caller may have already refreshed the lookup since this one was emitted
        if (this.latestLookupByCompany.get(companyId) === lookup) {
          this.cachedLookup.delete(companyId)
        }
        return this.getLookup().pipe(
          tap((fresh) =>
            getMissingJobNumbers(rows, fresh).forEach((jobNumber) =>
              fresh.knownMissingJobNumbers.add(jobNumber)
            )
          )
        )
      }),
      map((lookup) => applyJobLookup(rows, lookup))
    )
  }
}
